///////////////
// IMPORTS
///////////////
// schema
import * as ISchema from "@schema";

import { CSSProperties } from "@material-ui/core/styles/withStyles";
import { colors, greys, gradient } from "@clockwise/web-commons/src/styles/color.styles";
import { isOnSuggestionsFeed } from "@util/experiment.util";
import { articleUrls } from "@clockwise/client-commons/src/constants/help-center";
import { maybeWarnOutsideTests } from "@util/helpers.util";
import { TrackingEvents } from "@util/analytics.util";
// images used in tip/config cards
import { logoClockWhite } from "@img/logos";
import {
  suggestionPersonalCalendarSyncBg,
  suggestionColorsBg,
  suggestionSlackEnhancedBg,
  suggestionSetUpTeamBg,
  suggestionTravelTimeBg,
} from "@img/illustrations";

///////////////
// TYPES
///////////////
export interface IRichSuggestionConfig {
  illustration: string;
  illustrationHeight: number;
  illustrationBackground: string;
  colorHeader?: string;
  colorText?: string;
  actionBackground?: string;
  bodyBackground?: string;
  moreInfoLink?: string;
  moreInfoTracking?: string;
}

// suggestionType on the base suggestion object
// maps to SuggestionType
export enum ESuggestionType {
  Autopilot = "MeetingNotOnAutopilot",
  AutopilotAttendee = "AutopilotAttendee",
  Config = "MissingUserConfiguration",
  ConflictingMeeting = "ConflictingMeeting",
  ExpensiveMeeting = "ExpensiveMeeting",
  ExpensiveOrganizer = "ExpensiveOrganizer",
  LowFocusTime = "LowFocusTime",
  MissingAttendance = "MissingAttendance",
  OutOfOffice = "TeammateOutOfOffice",
  RecurringOneOnOneOrganizer = "RecurringOneOnOneOrganizer",
  RunningOutOfFocusTime = "RunningOutOfFocusTime",
  Shell = "Shell",
  Tip = "ChromeExtensionTip",
  WeekInReview = "WeekInReview",
  WeeklyAutopilotSummary = "WeeklyAutopilotSummary",
}

// __typenames of the payloads on suggestions
// maps to the '__typename' field on SuggestionResultPayload
export enum EPayloadType {
  Autopilot = "SuggestionResultMeetingNotOnAutopilotPayload",
  AutopilotAttendee = "SuggestionResultAutopilotAttendeePayload",
  Config = "SuggestionResultMissingUserConfigurationPayload",
  ConflictingMeeting = "SuggestionResultConflictingMeetingPayload",
  ExpensiveMeeting = "SuggestionResultExpensiveMeetingPayload",
  ExpensiveOrganizer = "SuggestionResultExpensiveOrganizerPayload",
  LowFocusTime = "SuggestionResultLowFocusTimePayload",
  MissingAttendance = "SuggestionResultMissingAttendancePayload",
  OutOfOffice = "SuggestionResultTeammateOutOfOfficePayload",
  RecurringOneOnOneOrganizer = "SuggestionResultRecurringOneOnOneOrganizerPayload",
  RunningOutOfFocusTime = "SuggestionResultRunningOutOfFocusTimePayload",
  Shell = "SuggestionResultShellPayload",
  Tip = "SuggestionResultChromeExtensionTipPayload",
  WeekInReview = "SuggestionResultWeekInReviewPayload",
  WeeklyAutopilotSummary = "SuggestionResultWeeklyAutopilotSummaryPayload",
}

// actiontype on suggestion actions
// maps to ISuggestionActionResultTypeEnum
export enum EActionType {
  Autopilot = "Autopilot",
  ConfigureSettings = "ConfigureSettings",
  Decline = "Decline",
  Fyi = "Fyi",
  Invite = "Invite",
  RemoveAttendee = "RemoveAttendee",
  RequestToMove = "RequestToMove",
  Reschedule = "Reschedule",
  ViewMeeting = "ViewMeeting",
  ViewProfile = "ViewProfile",
  ViewTeamDashboard = "ViewTeamDashboard",
}

// __typenames of the action payloads on suggestions
// maps to the '__typename' field on SuggestionActionPayload
export enum EActionPayloadType {
  Autopilot = "SuggestionActionAutopilotPayload",
  ConfigureSettings = "SuggestionActionConfigureSettingsPayload",
  Decline = "SuggestionActionDeclinePayload",
  Fyi = "SuggestionActionFyiPayload",
  Invite = "SuggestionActionInvitePayload",
  RemoveAttendee = "SuggestionActionRemoveAttendeePayload",
  RequestToMove = "SuggestionActionRequestToMovePayload",
  Reschedule = "SuggestionActionReschedulePayload",
  ViewMeeting = "SuggestionActionViewMeetingPayload",
  ViewProfile = "SuggestionActionViewProfilePayload",
  ViewTeamDashboard = "SuggestionActionViewTeamDashboardPayload",
}

// Tip Types
// maps schema TipType
export enum ETipType {
  FeedWelcome = "FeedWelcome",
  CheckOutTeamDashboard = "CheckOutTeamDashboard",
  CheckOutProfile = "CheckOutProfile",
  CheckOutRescheduler = "CheckOutRescheduler",
}

// Config Types
// maps schema ConfigurationType
export enum EConfigType {
  MeetingColors = "MeetingColors",
  PersonalCalendarSync = "PersonalCalendarSync",
  SettingUpTeam = "SettingUpTeam",
  SettingUpWorkingHours = "SettingUpWorkingHours",
  SlackStatusSync = "SlackStatusSync",
  TravelTime = "TravelTime",
}

//////////////////////////////
// SUGGESTIONS WHITELIST
//////////////////////////////
export const suggestionsWhitelist = [
  ESuggestionType.Autopilot,
  ESuggestionType.OutOfOffice,
  ESuggestionType.LowFocusTime,
  ESuggestionType.ExpensiveOrganizer,
  ESuggestionType.ExpensiveMeeting,
  ESuggestionType.ConflictingMeeting,
  ESuggestionType.WeekInReview,
  ESuggestionType.WeeklyAutopilotSummary,
];

export const tipConfigWhitelist = [
  ETipType.FeedWelcome,
  EConfigType.MeetingColors,
  EConfigType.PersonalCalendarSync,
  EConfigType.SlackStatusSync,
  EConfigType.SettingUpTeam,
  EConfigType.TravelTime,
];

export function isSuggestionInWhitelist(org: ISchema.IOrg, suggestion?: ISchema.IFeedSuggestion) {
  // if in the experiment, return all suggestion types
  if (org && isOnSuggestionsFeed(org)) {
    return true;
  }

  // check for empty/null suggesiton
  if (!suggestion) {
    return false;
  }

  const { suggestionType, suggestionPayload } = suggestion;
  maybeWarnOutsideTests(
    !!suggestionPayload.__typename,
    "no __typename on suggestionPayload, check fragments",
  );

  // check whitelist for tipType
  if (suggestionPayload.__typename === EPayloadType.Tip) {
    const tipType = suggestionPayload.tipType;
    maybeWarnOutsideTests(!!tipType, "no tipType on tip payload, check fragments");
    return tipConfigWhitelist.indexOf(tipType as any) > -1; // FIXME: TEMP FIX FOR GQL2TS ISSUES
  }

  // check whitelist for configType
  if (suggestionPayload.__typename === EPayloadType.Config) {
    const configType = suggestionPayload.configurationType;
    maybeWarnOutsideTests(!!configType, "no tipType on tip payload, check fragments");
    return tipConfigWhitelist.indexOf(configType as any) > -1; // FIXME: TEMP FIX FOR GQL2TS ISSUES
  }

  // check whitelist for type
  return suggestionsWhitelist.indexOf(suggestionType as any) > -1; // FIXME: TEMP FIX FOR GQL2TS ISSUES
}

///////////////
// HELPERS
///////////////
// gets the little upper-left label on suggestion cards
export function getSuggestionCardLabel(suggestionType: ISchema.SuggestionType, teamName?: string) {
  switch (
    suggestionType as any // FIXME: TEMP FIX FOR GQL2TS ISSUES
  ) {
    case ESuggestionType.LowFocusTime:
    case ESuggestionType.RunningOutOfFocusTime:
      return teamName ? teamName : "Team Analytics";
    case ESuggestionType.Tip:
    case ESuggestionType.Config:
      return "Tip";
    case ESuggestionType.ExpensiveMeeting:
    case ESuggestionType.ExpensiveOrganizer:
    case ESuggestionType.ConflictingMeeting:
    case ESuggestionType.MissingAttendance:
    case ESuggestionType.WeekInReview:
    case ESuggestionType.WeeklyAutopilotSummary:
    case ESuggestionType.Shell:
    default:
      return "Assistant";
  }
}

// somewhat hacky way to chop up the subheading text for styling
export function processSubheading(
  suggestionType: ISchema.SuggestionType,
  text: ISchema.IFeedSuggestionText,
): { text: string; styles?: CSSProperties }[] {
  switch (
    suggestionType as any // FIXME: TEMP FIX FOR GQL2TS ISSUES
  ) {
    case ESuggestionType.Autopilot:
      // MAYBE FIXME ?
      return [];
    case ESuggestionType.ExpensiveOrganizer:
      // split the string on '+' and 'per'
      const organizerRegex = /^(.*)(\+.*)(per.*)$/gi;
      const matches = organizerRegex.exec(text.subHeading);

      // if no matches, return  the subheading
      if (!matches || matches.length !== 4) {
        return [{ text: text.subHeading }];
      }

      // otherwise map out what we want for expensive organizer
      return [
        { text: matches[1] },
        { text: matches[2], styles: { color: colors.green.darker, fontWeight: "bold" } },
        { text: matches[3] },
      ];
    default:
      // everything else returns a regular subheading
      return [{ text: text.subHeading }];
  }
}

// pull the relevant help-center article
export function maybeGetMoreInfoLink(suggestionType: ISchema.SuggestionType) {
  switch (
    suggestionType as any // FIXME: TEMP FIX FOR GQL2TS ISSUES
  ) {
    case ESuggestionType.Autopilot:
      return articleUrls.autopilot;
    default:
      return null;
  }
}

// pull the relevant help-center article tracking
export function maybeGetMoreInfoTracking(suggestionType: ISchema.SuggestionType) {
  switch (
    suggestionType as any // FIXME: TEMP FIX FOR GQL2TS ISSUES
  ) {
    case ESuggestionType.Autopilot:
      return TrackingEvents.HELP.AUTOPILOT;
    default:
      return null;
  }
}

// gets the background-color info for tip / config types
export function getRichSuggestionConfig(
  tipOrConfigType: ISchema.TipType | ISchema.ConfigurationType,
  _org: ISchema.IOrg,
): IRichSuggestionConfig | null {
  const colorHeader = greys.white;
  const colorText = greys.white;

  switch (
    tipOrConfigType as any // FIXME: TEMP FIX FOR GQL2TS ISSUES
  ) {
    case ETipType.FeedWelcome:
      return {
        colorHeader,
        colorText,
        bodyBackground: colors.blueCool.darker,
        actionBackground: `rgba(54,	99,	135, 1)`,
        illustration: logoClockWhite,
        illustrationHeight: 80,
        illustrationBackground: gradient,
      };
    case EConfigType.MeetingColors:
      return {
        actionBackground: "rgba(221, 223, 244, 1)",
        illustration: suggestionColorsBg,
        illustrationHeight: 144,
        illustrationBackground: colors.blueCool.standard,
      };
    case EConfigType.PersonalCalendarSync:
      return {
        colorHeader,
        colorText,
        bodyBackground: colors.purple.standard,
        actionBackground: `rgba(138, 88, 140, 1)`,
        illustration: suggestionPersonalCalendarSyncBg,
        illustrationHeight: 144,
        illustrationBackground: colors.green.standard,
        moreInfoLink: articleUrls.personalCalSync,
        moreInfoTracking: TrackingEvents.HELP.PERSONAL_CALENDAR_SYNC,
      };
    case EConfigType.SlackStatusSync:
      const slackIllustration = suggestionSlackEnhancedBg;
      return {
        actionBackground: "rgba(221, 223, 244, 1)",
        illustration: slackIllustration,
        illustrationHeight: 144,
        illustrationBackground: colors.purple.darker,
      };
    case EConfigType.SettingUpTeam:
      return {
        colorHeader,
        colorText,
        bodyBackground: colors.blueDusk.lighter,
        actionBackground: `rgba(88, 131, 173, 1)`,
        illustration: suggestionSetUpTeamBg,
        illustrationHeight: 144,
        illustrationBackground: colors.blueDusk.lightest,
      };
    case EConfigType.TravelTime:
      return {
        colorHeader,
        colorText,
        bodyBackground: colors.blueCool.darker,
        actionBackground: `rgba(54,	99,	135, 1)`,
        illustration: suggestionTravelTimeBg,
        illustrationHeight: 144,
        illustrationBackground: gradient,
      };
    case ETipType.CheckOutProfile:
    case ETipType.CheckOutRescheduler:
    case ETipType.CheckOutTeamDashboard:
    case EConfigType.SettingUpWorkingHours:
    default:
      return null;
  }
}

// checks to see if we have a tip or config card and, if so, gets the styles for the card
export function maybeGetRichSuggestionConfig(
  suggestionPayload: ISchema.SuggestionResultPayload,
  org: ISchema.IOrg,
) {
  let tipOrConfigType: ISchema.TipType | ISchema.ConfigurationType | null = null;

  if (suggestionPayload.__typename === EPayloadType.Tip) {
    tipOrConfigType = suggestionPayload.tipType;
  } else if (suggestionPayload.__typename === EPayloadType.Config) {
    tipOrConfigType = suggestionPayload.configurationType;
  }

  return (!!tipOrConfigType && getRichSuggestionConfig(tipOrConfigType, org)) || null;
}
